import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";

// FOMO bias thresholds (%)
const FOMO_THRESHOLD_YELLOW = 20;
const FOMO_THRESHOLD_RED = 40;

// Revenge trading thresholds (count)
const REVENGE_THRESHOLD_YELLOW = 5;

// Loss aversion ratio thresholds (×)
const LOSS_AVERSION_THRESHOLD_YELLOW = 1.2;
const LOSS_AVERSION_THRESHOLD_RED = 2;

// Overconfidence score thresholds (%)
const OVERCONFIDENCE_THRESHOLD_GREEN = 60;

// Color constants — theme tokens (resolve at render in inline styles)
const COLOR_HEALTHY = "hsl(var(--edgely-pnl-positive))";
const COLOR_WARNING = "hsl(var(--warning))";
const COLOR_ALERT = "hsl(var(--edgely-pnl-negative))";
const COLOR_NEUTRAL = "hsl(var(--muted-foreground))";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function BiasTile({
  label,
  value,
  desc,
  color,
  barWidth,
  barColor,
  isDark,
}: {
  label: string;
  value: string;
  desc: string;
  color: string;
  barWidth?: number;
  barColor?: string;
  isDark: boolean;
}) {
  return (
    <div className="rounded-lg p-3 bg-muted/40 flex flex-col gap-1.5">
      <p
        className={`text-[10px] font-semibold tracking-widest uppercase ${
          isDark ? "widget-label" : "widget-label"
        }`}
      >
        {label}
      </p>
      <p className="text-xl font-bold font-mono" style={{ color }}>
        {value}
      </p>
      {barWidth !== undefined && barColor && (
        <div className="w-full bg-muted/30 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full transition-all"
            style={{
              width: Math.min(barWidth, 100) + "%",
              backgroundColor: barColor,
            }}
          />
        </div>
      )}
      <p className="text-[10px] text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

export default function BehavioralBiasesWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  if (!analyticsData) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const { behavioralBiases } = analyticsData;

  const fomoColor =
    behavioralBiases.fomoIndicator < FOMO_THRESHOLD_YELLOW
      ? COLOR_HEALTHY
      : behavioralBiases.fomoIndicator < FOMO_THRESHOLD_RED
      ? COLOR_WARNING
      : COLOR_ALERT;

  const revengeColor =
    behavioralBiases.revengeTradingCount === 0
      ? COLOR_HEALTHY
      : behavioralBiases.revengeTradingCount < REVENGE_THRESHOLD_YELLOW
      ? COLOR_WARNING
      : COLOR_ALERT;

  const lossAversionColor =
    behavioralBiases.lossAversionRatio <= 0
      ? COLOR_NEUTRAL
      : behavioralBiases.lossAversionRatio <= LOSS_AVERSION_THRESHOLD_YELLOW
      ? COLOR_HEALTHY
      : behavioralBiases.lossAversionRatio <= LOSS_AVERSION_THRESHOLD_RED
      ? COLOR_WARNING
      : COLOR_ALERT;

  const overconfColor =
    behavioralBiases.overconfidenceScore <= 0
      ? COLOR_NEUTRAL
      : behavioralBiases.overconfidenceScore >= OVERCONFIDENCE_THRESHOLD_GREEN
      ? COLOR_HEALTHY
      : COLOR_WARNING;

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={t.analytics.biasesTitle}
        isDark={isDark}
      />
      <CardContent className="px-5 pb-5 pt-1">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <BiasTile
            label={t.analytics.fomoIndicator}
            value={`${behavioralBiases.fomoIndicator.toFixed(1)}%`}
            desc={t.analytics.fomoIndicatorDesc}
            color={fomoColor}
            barWidth={Math.min(behavioralBiases.fomoIndicator, 100)}
            barColor={fomoColor}
            isDark={isDark}
          />
          <BiasTile
            label={t.analytics.revengeTradingCount}
            value={String(behavioralBiases.revengeTradingCount)}
            desc={t.analytics.revengeTradingCountDesc}
            color={revengeColor}
            isDark={isDark}
          />
          <BiasTile
            label={t.analytics.lossAversionRatio}
            value={
              behavioralBiases.lossAversionRatio > 0
                ? `${behavioralBiases.lossAversionRatio.toFixed(2)}×`
                : "—"
            }
            desc={t.analytics.lossAversionRatioDesc}
            color={lossAversionColor}
            barWidth={
              behavioralBiases.lossAversionRatio > 0
                ? Math.min((behavioralBiases.lossAversionRatio / 3) * 100, 100)
                : undefined
            }
            barColor={lossAversionColor}
            isDark={isDark}
          />
          <BiasTile
            label={t.analytics.overconfidenceScore}
            value={
              behavioralBiases.overconfidenceScore > 0
                ? `${behavioralBiases.overconfidenceScore.toFixed(0)}%`
                : "—"
            }
            desc={t.analytics.overconfidenceScoreDesc}
            color={overconfColor}
            barWidth={
              behavioralBiases.overconfidenceScore > 0
                ? behavioralBiases.overconfidenceScore
                : undefined
            }
            barColor={overconfColor}
            isDark={isDark}
          />
        </div>
      </CardContent>
    </Card>
  );
}
